"use client"

import type React from "react"

import { useEffect, useState } from "react"
import { useAppContext } from "../../../shared/functions/Context"
import RequestCard from "./RequestCard"
import type { RatingStatus } from "../../../shared/models/three-sixty-feedback-models/Committee"
import { Clock, CheckCircle2, Play, Activity, FileText } from "lucide-react"

const UserRequests = () => {
  const { store, api } = useAppContext()
  const me = store.auth.meJson

  const assignments = store.templateRating?.all
    .filter((rate) => rate.asJson.raterId === me?.uid || "")
    .map((rate) => rate.asJson)

  const [loading, setLoading] = useState(false)

  const toDo = assignments.filter((assignment) => assignment.status === "Not Started")
  const inProgress = assignments.filter((assignment) => assignment.status === ("In Progress" as RatingStatus))
  const completed = assignments.filter((assignment) => assignment.status === "Completed")

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        await api.templateRating.getAll()
      } catch (error) {
        console.error("Error fetching data:", error)
      }
      setLoading(false)
    }

    fetchData()
  }, [api.templateRating, me?.uid, store.templateRating])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="h-5 w-5 text-blue-600 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  const KanbanColumn = ({
    title,
    items,
    icon,
    color,
    bgColor,
  }: {
    title: string
    items: any[]
    icon: React.ReactNode
    color: string
    bgColor: string
  }) => (
    <div className="flex-1 min-w-0">
      <div className={`${bgColor} rounded-xl p-4 mb-4 border border-opacity-20`}>
        <div className="flex items-center gap-3 mb-2">
          <div className={`p-2 ${color} rounded-lg`}>{icon}</div>
          <div>
            <h3 className="text-lg font-bold text-slate-800">{title}</h3>
            <p className="text-sm text-slate-600">{items.length} items</p>
          </div>
        </div>
      </div>

      <div className="space-y-4 max-h-[600px] overflow-y-auto">
        {items.length > 0 ? (
          items
            .sort((a, b) => new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime())
            .map((assignment) => (
              <RequestCard key={assignment.id} assignment={assignment} templateId={assignment.templateId} />
            ))
        ) : (
          <div className="text-center py-8">
            <div className="p-4 bg-slate-100 rounded-full w-fit mx-auto mb-4">
              <FileText className="h-8 w-8 text-slate-400" />
            </div>
            <p className="text-slate-500">No {title.toLowerCase()} items</p>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <FileText className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">My Requests</h2>
            <p className="text-slate-600">Manage your rating assignments and track progress</p>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="flex items-center gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-600">{toDo.length}</div>
            <div className="text-xs text-slate-600">To Do</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{inProgress.length}</div>
            <div className="text-xs text-slate-600">In Progress</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{completed.length}</div>
            <div className="text-xs text-slate-600">Completed</div>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-6 overflow-x-auto pb-4">
        <KanbanColumn
          title="To Do"
          items={toDo}
          icon={<Clock className="h-5 w-5 text-amber-600" />}
          color="bg-amber-100"
          bgColor="bg-amber-50"
        />

        <KanbanColumn
          title="In Progress"
          items={inProgress}
          icon={<Play className="h-5 w-5 text-blue-600" />}
          color="bg-blue-100"
          bgColor="bg-blue-50"
        />

        <KanbanColumn
          title="Completed"
          items={completed}
          icon={<CheckCircle2 className="h-5 w-5 text-green-600" />}
          color="bg-green-100"
          bgColor="bg-green-50"
        />
      </div>

      {/* Empty State */}
      {assignments.length === 0 && (
        <div className="text-center py-12">
          <div className="p-4 bg-slate-100 rounded-full w-fit mx-auto mb-4">
            <FileText className="h-12 w-12 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Requests Available</h3>
          <p className="text-slate-600">You don't have any rating requests at the moment.</p>
        </div>
      )}
    </div>
  )
}

export default UserRequests
